import { Menu, X } from 'lucide-react';
import { FC, useEffect, useState } from 'react';

interface NavbarProps {
  isScrolled: boolean;
}

const navItems = [
  { name: 'Features', href: '#features' },
  { name: 'How It Works', href: '#how-it-works' },
  { name: 'Pricing', href: '#pricing' },
  { name: 'Testimonials', href: '#testimonials' },
];

const Navbar: FC<NavbarProps> = ({ isScrolled }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeItem, setActiveItem] = useState('');

  // Handle scroll to update active section based on viewport
  useEffect(() => {
    const handleScroll = () => {
      const sections = navItems.map((item) => document.querySelector(item.href));
      const scrollPosition = window.scrollY + 100; // Offset for better detection

      sections.forEach((section) => {
        if (!section) return;

        const sectionTop = section.offsetTop;
        const sectionHeight = section.clientHeight;

        if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
          setActiveItem(section.getAttribute('id') || '');
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  return (
    <nav
      className={`py-4 sticky top-0 z-40 transition-all duration-300 ${
        isScrolled
          ? 'bg-background/90 backdrop-blur-md shadow-sm border-b border-border'
          : 'bg-transparent'
      }`}>
      <div className='container mx-auto px-4 flex justify-between items-center'>
        {/* Logo Section */}
        <div className='flex items-center space-x-3'>
          <div className='h-10 w-10 rounded-md bg-primary flex items-center justify-center transition-all duration-300 hover:scale-105 hover:shadow-md'>
            <span className='text-primary-foreground font-bold text-lg'>RS</span>
          </div>
          <span className='font-bold text-xl text-foreground tracking-tight'>
            ResumeReady
          </span>
        </div>

        {/* Desktop Navigation */}
        <div className='hidden lg:flex items-center space-x-8'>
          {navItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className={`text-base font-medium transition-all duration-300 relative group ${
                activeItem === item.href.substring(1)
                  ? 'text-primary'
                  : 'text-foreground hover:text-primary'
              }`}>
              {item.name}
              <span
                className={`absolute bottom-0 left-0 h-0.5 bg-primary transition-all duration-300 ${
                  activeItem === item.href.substring(1)
                    ? 'w-full'
                    : 'w-0 group-hover:w-full'
                }`}></span>
            </a>
          ))}
        </div>

        {/* Authentication Buttons */}
        <div className='hidden lg:flex items-center space-x-4'>
          <button className='text-foreground hover:text-primary transition-all duration-300 cursor-pointer'>
            Sign In
          </button>
          <button className='bg-primary text-primary-foreground rounded-md px-5 py-2 text-sm font-medium transition-all duration-300 hover:bg-primary/90 hover:shadow-md'>
            Get Started
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button
          className='lg:hidden text-foreground p-1 rounded-md hover:bg-secondary/20 transition-all duration-300 cursor-pointer'
          onClick={toggleMenu}
          aria-label='Toggle menu'>
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden bg-card border-t border-border transition-all duration-300 overflow-hidden ${
          isMenuOpen ? 'max-h-screen py-4' : 'max-h-0'
        }`}>
        <div className='container mx-auto px-4 flex flex-col space-y-4'>
          {navItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className={`text-base py-2 font-medium transition-all duration-300 ${
                activeItem === item.href.substring(1)
                  ? 'text-primary'
                  : 'text-foreground hover:text-primary'
              }`}
              onClick={() => setIsMenuOpen(false)}>
              {item.name}
            </a>
          ))}
          <div className='flex flex-col space-y-3 pt-2 border-t border-border'>
            <button className='w-full text-center py-2 text-foreground hover:text-primary transition-all duration-300 cursor-pointer'>
              Sign In
            </button>
            <button className='w-full cursor-pointer text-center bg-primary text-primary-foreground rounded-md py-2 font-medium transition-all duration-300 hover:bg-primary/90 '>
              Get Started
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
